import { ref, type Ref } from 'vue'
import { useMessage } from 'naive-ui'

// Owns the "set loading, run the action, toast on success/failure, clear
// loading" lifecycle that AgentRepoDevcontainerActions.vue's handlers
// (start/stop/reboot/rebuild/rebuild-no-cache/update-cli/auto-update
// toggle) previously each duplicated (#897).
//
// Requires NMessageProvider in the tree (App.vue mounts one) — same
// requirement the duplicated handlers already had via their own
// `useMessage()` call.
//
// Usage:
//
//   const { loading: starting, run: start } = useAsyncAction(
//     () => store.start(props.repo.slug),
//     { successMessage: () => `${props.repo.slug} devcontainer starting`, failureLabel: 'Failed to start devcontainer' },
//   )
//
// Args pass through `run` to the action and to `successMessage`, so a
// handler driven by an emitted value (e.g. a checkbox's `@update:checked`)
// still works:
//
//   const { run: toggleAutoUpdate } = useAsyncAction(
//     (checked: boolean) => store.update(props.repo.slug, { auto_update_cli: checked }),
//     { failureLabel: 'Failed to update auto-update setting' },
//   )
//
// Omit `successMessage` for an action that should stay silent on success
// (e.g. a settings toggle) — only `failureLabel` is required.

export interface UseAsyncActionOptions<Args extends unknown[]> {
  successMessage?: (...args: Args) => string
  failureLabel: string
}

export interface UseAsyncAction<Args extends unknown[]> {
  loading: Ref<boolean>
  run: (...args: Args) => Promise<void>
}

export function useAsyncAction<Args extends unknown[] = []>(
  action: (...args: Args) => Promise<void>,
  options: UseAsyncActionOptions<Args>,
): UseAsyncAction<Args> {
  const loading = ref(false)
  const message = useMessage()

  async function run(...args: Args): Promise<void> {
    loading.value = true
    try {
      await action(...args)
      if (options.successMessage) message.success(options.successMessage(...args))
    } catch (e) {
      message.error(e instanceof Error ? e.message : options.failureLabel)
    } finally {
      loading.value = false
    }
  }

  return { loading, run }
}
